<template>
  <List
    class="tag-list"
    titleI18nKey="admin.crm.title.tag.task"
    default-expand-all
    :loading="loading"
    :listData="listData"
    :tableColumns="tableColumns"
    :actionBtn="actionBtn"
    :pagination="pagination"
    @create="handleCreate"
  >
    <template #page-bottom>
      <DialogWrapper
        titleI18nKey="admin.crm.message.tag.taskDeleteConfirm"
        saveBtnLabelI18nKey="common.btn.confirm"
        closeOnCancel
        :loading="dialog.loading"
        :show.sync="dialog.show"
        @save="handleDelete"
      ></DialogWrapper>
    </template>
  </List>
</template>

<script>
import _ from 'lodash'
import { mapActions } from 'vuex'
import { CONDITIONS_RELATION_TYPE, CONDITION_RELATION, CONDITION_RULES } from '@/constant/crm'
import DialogWrapper from '@/components/dialog/dialogWrapper.vue'
import List from '@/components/tableList/allowDragSortTable.vue'
import { isEn } from '@/i18n'

export default {
  components: {
    List,
    DialogWrapper
  },
  data () {
    return {
      loading: false,
      listData: [],
      actionBtn: {
        edit: {
          show: (row) => {
            if (row.isTask) {
              return !row.status
            }
            return false
          },
          onclick: this.handleEdit
        },
        delete: {
          show: (row) => {
            if (row.isTask) {
              return !row.status
            }
            return false
          },
          onclick: this.openDialog
        }
      },
      pagination: {
        page: 1,
        pageSize: 10,
        total: 0
      },
      dialog: {
        show: false,
        loading: false,
        data: null,
        dataIndex: null
      }
    }
  },
  computed: {
    tableColumns () {
      const columns = [
        {
          dataKey: 'name',
          labelI18nKey: 'admin.crm.label.tag.taskName',
          formatter: (data, row) => {
            if (row.isTask) {
              return data
            }
          }
        },
        {
          dataKey: 'name',
          labelI18nKey: 'admin.crm.label.tag.tagName',
          formatter: (data, row) => {
            if (!row.isTask) {
              return data
            }
          }
        },
        {
          dataKey: 'condition',
          labelI18nKey: 'admin.crm.label.tag.judgingCriteria',
          formatter: (data, row) => {
            if (!row.isTask) {
              return this.generateJudgingCriteria(data, row)
            }
          }
        },
        {
          dataKey: 'status',
          labelI18nKey: 'admin.crm.label.tag.taskStatus',
          formatter: (data, row) => {
            if (row.isTask) {
              if (data) {
                return this.$t('admin.crm.label.tag.taskStatusSuccess')
              }
              if (row.execute_time) {
                return this.$t('admin.crm.label.tag.taskStatusDoing')
              } else {
                return this.$t('admin.crm.label.tag.taskStatusPadding')
              }
            }
          }
        }
      ]
      return columns
    }
  },
  created () {
    this.getTagList()
  },
  methods: {
    ...mapActions('crm', ['searchTagTask', 'deleteTagTask']),
    async getTagList () {
      this.loading = true
      try {
        const resp = await this.searchTagTask({
          page: this.pagination.page,
          page_size: this.pagination.pageSize
        })
        if (resp) {
          this.listData = this.dealOriginListData(resp.task_list)
          this.pagination.total = resp.total_count
        }
      } finally {
        this.loading = false
      }
    },
    dealOriginListData (originData) {
      const listData = []
      _.forEach(originData, item => {
        const dataItem = {
          ...item,
          id: `${item.id}`,
          isTask: true,
          children: []
        }
        if (dataItem.tag_list && dataItem.tag_list.length > 0) {
          _.forEach(dataItem.tag_list, tag => {
            dataItem.children.push({
              ...tag,
              id: `task_${item.id}_tag_${tag.id}`,
              isTask: false
            })
          })
        }
        listData.push(dataItem)
      })
      return listData
    },
    generateJudgingCriteria (condition, tag) {
      let judgingCriteria = this.$t(`admin.crm.option.tag.${tag.match_condition_rule}`)
      if (tag.match_condition_rule !== CONDITION_RULES.no) {
        judgingCriteria = judgingCriteria.concat(isEn() ? ': ' : '：')
      }
      return judgingCriteria.concat(this.generateConditionDisplayValue(condition))
    },
    generateConditionDisplayValue (condition) {
      const conditionsDisplayValue = []
      JSON.parse(condition).forEach(item => {
        const items = []
        items.push(this.$t(`admin.crm.condition.name.${item.name}`))
        if (item.relationType === CONDITIONS_RELATION_TYPE.CONTAINS) {
          items.push(this.$t(`admin.crm.condition.relation.${item.relation}`))
          items.push(this.convertConditionValue(item))
        } else {
          items.push(this.convertConditionValue(item))
          items.push(this.$t(`admin.crm.condition.relation.${item.relation}`))
        }
        conditionsDisplayValue.push(items.join(' '))
      })

      return conditionsDisplayValue.join('; ')
    },
    convertConditionValue (condition) {
      let conditionValue = condition.value
      if (CONDITIONS_RELATION_TYPE.CONTAINS === condition.relationType) {
        const joinKey = this.$t('common.or')
        if (condition.name === 'gender') {
          conditionValue = _.map(conditionValue, item => {
            if (item === 'F') {
              return this.$t('common.gender.female')
            } else if (item === 'N') {
              return this.$t('common.gender.nonBinary')
            } else if (item === 'P') {
              return this.$t('common.gender.preferNotToDisclose')
            } else {
              return this.$t('common.gender.male')
            }
          }).join(joinKey)
        } else {
          conditionValue = conditionValue.join(joinKey)
        }
      } else if (CONDITION_RELATION.BETWEEN === condition.relation) {
        conditionValue = `${conditionValue[0]} - ${conditionValue[1]}`
      }

      return conditionValue
    },
    handleBack () {
      this.$router.push({ name: 'tagList' })
    },
    handleCreate () {
      this.$router.push({ name: 'tagTaskAdd' })
    },
    handleEdit (rowData) {
      this.$router.push({
        name: 'tagTaskEdit',
        params: {
          taskId: rowData.id
        }
      })
    },
    openDialog (rowData, index) {
      this.dialog.show = true
      this.dialog.data = rowData
      this.dialog.dataIndex = index
    },
    async handleDelete () {
      this.dialog.loading = true
      try {
        await this.deleteTagTask(this.dialog.data.id)
      } finally {
        this.dialog.show = false
        this.dialog.loading = false
        this.getTagList()
      }
    },
    handlePaginationChange () {
      this.getTagList()
    }
  }
}
</script>

<style lang="scss" scoped>
.tag-list {

  ::v-deep {
    .table-header {
      position: relative;

      .back-btn {
        position: absolute;
        left: 0;
      }
    }
  }

  .status-container {
    position: relative;
    .el-switch {
      ::v-deep {
        .el-switch__label {
          display: none;
          position: absolute;
          height: 100%;
          line-height: 22px;
          color: #ffffff;
          z-index: 1;
          margin: 0;
          &.is-active {
            display: inline-block;
          }
        }
        .el-switch__label--left {
          left: 22px;
        }
        .el-switch__label--right {
          left: 10px;
        }
      }
    }
    .switch-label {
      position: absolute;
      width: 24px;
      height: 100%;
      top: -2px;
    }
    .on {
      left: 7px;
    }
    .off {
      left: 20px;
    }
  }
  .dialog-footer {
    .delete-btn {
      margin-left: 35px;
      float: left;
    }
  }
  .tag-pagination {
    margin-top: 20px;
    display: flex;
    justify-content: flex-end;
  }
}
</style>
